// The SPACES tree of Work's sidebar (design spec §4 "Work: … SPACES tree (Work OS Sidebar tree:
// drag-nest, Arrow keys)"; Work OS sidebar.tsx): groups of spaces, spaces, and each space's
// boards. A `tree` with `treeitem` rows: ArrowUp/Down move, ArrowRight opens a row or enters it,
// ArrowLeft closes it or goes to its parent, Home/End jump, Enter follows the link.
//
// WORK-OS (the founder's "work is mainly docs"): boards are the main rows under a space, each
// with its coloured mark and its open-task count (ClickUp's list rows); a space's pages fold
// into one "Docs" row that opens the Docs screen filtered to the space, with the page tree
// behind an opt-in chevron. Every row has its controls on hover: a space gets "+" (Add board,
// Add docs) and a
// menu (New board, New doc, Star, Rename, Archive); a board gets Star and a menu (Open, New
// task, Copy link, Rename, Archive); Docs gets "+ page". wp/ACTIONS: spaces and boards also show
// Star, Rename and Archive (asks first) as hover icons and a grip for the drag (move); a group
// shows Rename and Remove (asks first; its spaces return to the top level).
//
// BOARD-ORDER: boards and spaces have a shared order on the server (`position`). A board row
// shows a grip on hover and drags before another board (any space) or onto a space (its end);
// Alt+Shift+ArrowUp/Down moves the focused board (or, for company admins, space) one place; its
// "…" menu has Move up / down and Move to space. A company admin's space dropped at the top level
// moves it in the company's order (everyone sees it); groups stay the person's own.
//
// Drag to nest, three kinds:
// - a space onto a group puts it in the group; onto another space (or the "end" strip) puts it
//   there at the top level, out of any group. This is the person's own arrangement
//   (arrangement.ts): the contracts give spaces no parent or order.
// - a task card dragged from a board view onto a board row moves the task to that board
//   (`POST tasks/{id}/move`, the server's rule maps the status).
// - a board onto a board row goes before it; onto a space row, to that space's end
//   (`POST boards/{id}/move`, optimistic).
//
// MEMBER-UX: for owners and admins, a space's "…" menu also holds "Space settings" (name,
// privacy, "Members can: View / Edit"); the dialog is the sidebar's (`onSpaceSettings`).
//
// WORKFORCE-SPACES: no space gets a Docs row automatically. A space shows Docs when it has
// pages, or when someone chose the space's "+" › Add docs (`settings.docs`); the "+" offers
// Add board and Add docs. An empty Docs row nobody asked for is gone (nothing was stored).
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  BoardMark,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  toast,
} from "@companyos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  ArchiveIcon,
  BookOpenIcon,
  BriefcaseIcon,
  Building2Icon,
  ChevronRightIcon,
  EllipsisIcon,
  FileTextIcon,
  FolderIcon,
  GripVerticalIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  SquareKanbanIcon,
  StarIcon,
  Trash2Icon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { type DragEvent, type KeyboardEvent, type ReactNode, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import {
  arrange,
  canDrop,
  type Drop,
  followCompanyOrder,
  moveSpace,
  removeGroup,
  renameGroup,
  type SpaceGroup,
  type Tree,
  useFavorites,
} from "./arrangement";
import { boardsOf, orderedSpaces, stepTargets } from "./board-order";
import { useBoardWrites } from "./board-writes";
import { HealthDot, useClientHealth } from "./client-health";
import { SharedMarker } from "./space-shares";
import { useTaskWrites } from "./task-writes";
import { BoardMenu, type ContainerIconActions, RenameGroupDialog, SpaceMenu } from "./work-dialogs";
import { type BoardSummary, invalidateWork, prefetchBoard } from "./work-queries";

type Space = Awaited<ReturnType<Api["client"]["spaces"]["list"]>>["data"][number];
type Board = Awaited<ReturnType<Api["client"]["boards"]["list"]>>["data"][number];
export type PageRow = Awaited<ReturnType<Api["client"]["pages"]["list"]>>["data"][number];
export type NewPageTarget = { spaceId: string; parentPageId: string | null; parentTitle?: string };

/** Sibling order of the page tree (`@companyos/domain-work/pages` `bySiblingOrder`). */
const siblingOrder = (a: PageRow, b: PageRow) =>
  a.position - b.position || a.title.localeCompare(b.title) || a.id.localeCompare(b.id);

export const SPACE_ICON = {
  team: UsersIcon,
  project: BriefcaseIcon,
  client: Building2Icon,
  personal: UserIcon,
} as const;

const SPACE_MIME = "application/x-companyos-space";
const BOARD_MIME = "application/x-companyos-board";
/** The "before this row" drop line. */
const DROP_BEFORE =
  "before:absolute before:inset-x-1 before:-top-px before:h-0.5 before:rounded-full before:bg-primary";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ROW =
  "group/tree-row relative mx-2 flex min-h-9 items-center gap-2 rounded-md pr-2 text-sm text-sidebar-foreground outline-none transition-colors duration-(--dur-fast) ease-out hover:bg-muted hover:text-foreground pointer-coarse:min-h-11 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid aria-[current=page]:bg-primary-soft aria-[current=page]:font-medium aria-[current=page]:text-primary-text";
const DROP_INSIDE = "bg-primary-soft ring-2 ring-primary ring-inset";
/** The row's hover controls: hidden until hover or focus within (always on coarse pointers). */
const ACTIONS =
  "absolute top-1/2 right-2 z-10 flex -translate-y-1/2 items-center gap-0.5 opacity-0 group-hover/tree-node:opacity-100 group-focus-within/tree-node:opacity-100 pointer-coarse:opacity-100";
const ACTION =
  "flex size-6 items-center justify-center rounded-sm text-faint hover:bg-sunken hover:text-foreground focus-visible:opacity-100";

export function SpacesTree({
  api,
  company,
  spaces,
  boards,
  summaries = new Map(),
  pages = [],
  sharedPages = [],
  onNewPage,
  onNewBoard,
  onNewTask,
  onSpaceSettings,
  tree,
  onTreeChange,
}: {
  api: Api;
  company: CompanyAccess;
  spaces: Space[];
  boards: Board[];
  /** WORK-OS: board id → its summary (the open count on the row). */
  summaries?: ReadonlyMap<string, BoardSummary>;
  /** M7-02: the pages the caller may view (`pages.list`), across spaces. */
  pages?: PageRow[];
  /** MULTI-SPACE-ACCESS: pages shared into more spaces; each also shows in those Docs, marked. */
  sharedPages?: readonly { object_id: string; space_id: string }[];
  onNewPage?: (target: NewPageTarget) => void;
  onNewBoard?: (spaceId: string) => void;
  onNewTask?: (board: Board) => void;
  /** MEMBER-UX: owners and admins only (the sidebar passes it for them). */
  onSpaceSettings?: ((space: Space) => void) | undefined;
  tree: Tree;
  onTreeChange: (tree: Tree) => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search as { space?: string } });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const favorites = useFavorites(company.id);
  // BIZ-CLIENT-HEALTH: a coloured dot beside each client space (one cached query).
  const health = useClientHealth(
    api,
    company.id,
    spaces.some((s) => s.kind === "client" && !s.archived_at),
  );
  const [closed, setClosed] = useState<Set<string>>(() => new Set());
  // The page tree under a Docs row is opt-in (the person's choice per space, by `docs:{id}`);
  // the space of the page open on screen shows its tree until folded, so the page is in the tree.
  const [docsChoice, setDocsChoice] = useState<Map<string, boolean>>(() => new Map());
  const currentPageId = /\/work\/p\/([0-9a-f-]{36})$/i.exec(pathname)?.[1];
  const currentPageSpace = currentPageId
    ? (pages.find((p) => p.id === currentPageId)?.space_id ?? null)
    : null;
  const docsIsOpen = (key: string) => docsChoice.get(key) ?? currentPageSpace === key.slice(5);
  const [dragSpace, setDragSpace] = useState<string | null>(null);
  const [dragBoard, setDragBoard] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; kind: "inside" | "before" } | null>(null);
  const root = useRef<HTMLUListElement>(null);
  const [groupEdit, setGroupEdit] = useState<{
    kind: "rename" | "remove";
    group: SpaceGroup;
  } | null>(null);
  const slug = company.slug;
  const name = (id: string) => spaces.find((s) => s.id === id)?.name ?? "";
  // BOARD-ORDER: spaces outside the person's groups follow the company's order.
  const ids = orderedSpaces(spaces).map((s) => s.id);
  const rows = arrange(tree, ids);
  const admin = isManager(company);
  const order = useBoardWrites(api, company);
  const spaceNamed = (id: string) => spaces.find((s) => s.id === id)?.name;
  /** Moves a board (by drag or key) before `beforeId` of `spaceId`, null: its end. */
  const moveBoard = (id: string, spaceId: string, beforeId: string | null) => {
    const b = boards.find((x) => x.id === id);
    if (b && b.id !== beforeId) order.move(b, spaceId, beforeId, spaceNamed(spaceId));
  };
  /** A company admin's move of a space in the shared order; the person's top level gives way. */
  const shareSpaceMove = (id: string, beforeId: string | null) => {
    const sp = spaces.find((x) => x.id === id);
    if (!sp || sp.kind === "personal" || id === beforeId) return false;
    order.moveSpace(sp, beforeId);
    onTreeChange(followCompanyOrder(tree, id));
    return true;
  };
  const first = rows[0];
  const firstId = first ? (first.kind === "group" ? first.group.id : first.spaceId) : null;

  // WORK-PERF: the card leaves its board at once (and joins the target, when it is cached).
  const writes = useTaskWrites(api, company);
  const moveTask = {
    mutate: (v: { taskId: string; board: Board }) =>
      writes.move(v.taskId, v.board.id, {
        onSuccess: (t) =>
          toast.add({ title: `#${t.number} moved to ${v.board.name}`, type: "success" }),
      }),
  };

  const toggleIn = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const toggle = (id: string) => {
    if (id.startsWith("docs:")) {
      const open = docsIsOpen(id);
      setDocsChoice((m) => new Map(m).set(id, !open));
    } else setClosed((s) => toggleIn(s, id));
  };

  const dropSpace = (drop: Drop) => {
    const before = drop.kind === "top" ? drop.beforeId : undefined;
    const shared =
      admin &&
      dragSpace !== null &&
      drop.kind === "top" &&
      (before === null || spaces.some((x) => x.id === before && x.kind !== "personal"));
    if (shared && dragSpace && shareSpaceMove(dragSpace, before ?? null)) {
      toast.add({
        title: `${name(dragSpace)} moved`,
        description: "Everyone in the company sees the new order.",
      });
    } else if (dragSpace && canDrop(tree, dragSpace, drop)) {
      onTreeChange(moveSpace(tree, ids, dragSpace, drop));
      toast.add({
        title:
          drop.kind === "group"
            ? `${name(dragSpace)} moved into ${tree.groups.find((g) => g.id === drop.groupId)?.name ?? "the group"}`
            : `${name(dragSpace)} moved`,
        description: "Only your sidebar changes.",
      });
    }
    setDragSpace(null);
    setOver(null);
  };

  function onKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    const items = Array.from(
      root.current?.querySelectorAll<HTMLElement>('[role="treeitem"]') ?? [],
    );
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    const item = items[i] as HTMLElement;
    const expanded = item.getAttribute("aria-expanded");
    const focus = (el: HTMLElement | undefined) => {
      if (!el) return;
      e.preventDefault();
      for (const x of items) x.tabIndex = -1;
      el.tabIndex = 0;
      el.focus();
    };
    // BOARD-ORDER: Alt+Shift+ArrowUp/Down moves the focused board (an admin's space) one place.
    if (e.altKey && e.shiftKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      const id = item.dataset.nodeId ?? "";
      const dir = e.key === "ArrowUp" ? "up" : "down";
      const b = boards.find((x) => x.id === id);
      if (b && item.dataset.boardRow !== undefined) {
        e.preventDefault();
        const to = stepTargets(
          boardsOf(boards, b.space_id).map((x) => x.id),
          b.id,
        )[dir];
        if (to !== undefined) order.move(b, b.space_id, to);
        return;
      }
      const sp = spaces.find((x) => x.id === id);
      if (admin && sp && sp.kind !== "personal" && item.dataset.spaceRow !== undefined) {
        e.preventDefault();
        const shared = orderedSpaces(spaces)
          .filter((x) => x.kind !== "personal" && !x.archived_at)
          .map((x) => x.id);
        const to = stepTargets(shared, sp.id)[dir];
        if (to !== undefined) shareSpaceMove(sp.id, to);
      }
      return;
    }
    if (e.key === "ArrowDown") focus(items[i + 1]);
    else if (e.key === "ArrowUp") focus(items[i - 1]);
    else if (e.key === "Home") focus(items[0]);
    else if (e.key === "End") focus(items.at(-1));
    else if (e.key === "ArrowRight") {
      if (expanded === "false") {
        e.preventDefault();
        toggle(item.dataset.nodeId ?? "");
      } else if (expanded === "true") focus(items[i + 1]);
    } else if (e.key === "ArrowLeft") {
      if (expanded === "true") {
        e.preventDefault();
        toggle(item.dataset.nodeId ?? "");
      } else {
        const level = Number(item.getAttribute("aria-level"));
        focus(
          items
            .slice(0, i)
            .reverse()
            .find((x) => Number(x.getAttribute("aria-level")) < level),
        );
      }
    }
  }

  /** A hover control on a row (stops the row's link; `tabIndex` -1 keeps the tree's one tab stop). */
  const action = (label: string, onClick: () => void, icon: ReactNode, cls?: string) => (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      title={label}
      className={cn(ACTION, cls)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      {icon}
    </button>
  );
  /** wp/ACTIONS: a board's or space's Rename and Archive as hover icons (its menu's dialogs). */
  const renameArchive =
    (name: string) =>
    ({ rename, archive }: ContainerIconActions) => (
      <>
        {action(`Rename ${name}`, rename, <PencilIcon aria-hidden="true" className="size-3.5" />)}
        {action(
          `Archive ${name}`,
          archive,
          <ArchiveIcon aria-hidden="true" className="size-3.5" />,
          "hover:text-destructive",
        )}
      </>
    );
  /** A space's Star on hover (on always while starred), as a board row's. */
  const starAction = (name: string, starred: boolean, toggleStar: () => void) =>
    action(
      starred ? `Unstar ${name}` : `Star ${name}`,
      toggleStar,
      <StarIcon
        aria-hidden="true"
        className={cn("size-3.5", starred && "fill-current text-warning")}
      />,
      starred ? "opacity-100" : undefined,
    );

  const boardRow = (b: Board, level: number) => {
    const href = `/c/${slug}/work/b/${b.id}`;
    const target = over?.id === b.id;
    const boardBefore = target && over?.kind === "before";
    const open = summaries.get(b.id)?.open;
    const fav = { kind: "board" as const, id: b.id };
    const starred = favorites.has(fav);
    return (
      <li key={b.id} role="none">
        <div className="group/tree-node relative">
          <Link
            to="/c/$slug/work/b/$boardId"
            params={{ slug, boardId: b.id }}
            role="treeitem"
            aria-level={level}
            aria-current={pathname === href ? "page" : undefined}
            aria-label={open !== undefined ? `${b.name}, ${open} open` : b.name}
            tabIndex={-1}
            data-node-id={b.id}
            data-board-row=""
            aria-keyshortcuts="Alt+Shift+ArrowUp Alt+Shift+ArrowDown"
            draggable={!b.archived_at}
            className={cn(
              ROW,
              "pr-14 group-hover/tree-node:pr-28 group-focus-within/tree-node:pr-28",
              boardBefore ? DROP_BEFORE : target && DROP_INSIDE,
              dragBoard === b.id && "opacity-40",
            )}
            onDragStart={(e: DragEvent) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData(BOARD_MIME, b.id);
              setDragBoard(b.id);
            }}
            onDragEnd={() => {
              setDragBoard(null);
              setOver(null);
            }}
            // WORK-PERF: the board, its views and its default view's tasks load while the pointer
            // is still on the way (or the row has keyboard focus).
            onPointerEnter={() => prefetchBoard(queryClient, api, company.id, b.id)}
            onFocus={() => prefetchBoard(queryClient, api, company.id, b.id)}
            onDragOver={(e: DragEvent) => {
              if (dragSpace || dragBoard === b.id) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setOver({ id: b.id, kind: dragBoard ? "before" : "inside" });
            }}
            onDragLeave={() => setOver((o) => (o?.id === b.id ? null : o))}
            onDrop={(e: DragEvent) => {
              if (dragSpace) return;
              e.preventDefault();
              setOver(null);
              if (dragBoard) {
                moveBoard(dragBoard, b.space_id, b.id);
                setDragBoard(null);
                return;
              }
              const id = e.dataTransfer.getData("text/plain");
              if (UUID.test(id)) moveTask.mutate({ taskId: id, board: b });
            }}
          >
            <Indent level={level} />
            {b.archived_at ? null : (
              <GripVerticalIcon
                aria-hidden="true"
                data-slot="tree-grip"
                className="absolute left-1 size-3.5 cursor-grab text-faint opacity-0 group-hover/tree-node:opacity-100 pointer-coarse:hidden"
              />
            )}
            <BoardMark name={b.name} color={b.color} size="xs" className="ml-0.5" />
            <span className="min-w-0 flex-1 truncate">{b.name}</span>
            {open !== undefined && open > 0 ? (
              <span
                data-slot="tree-open-count"
                className="text-2xs text-muted-foreground tabular-nums group-hover/tree-node:invisible"
              >
                {open}
              </span>
            ) : null}
          </Link>
          <span className={ACTIONS}>
            {action(
              starred ? `Unstar ${b.name}` : `Star ${b.name}`,
              () => favorites.toggle(fav, b.name),
              <StarIcon
                aria-hidden="true"
                className={cn("size-3.5", starred && "fill-current text-warning")}
              />,
              starred ? "opacity-100" : undefined,
            )}
            <BoardMenu
              api={api}
              company={company}
              board={b}
              star={{ on: starred, toggle: () => favorites.toggle(fav, b.name) }}
              icons={renameArchive(b.name)}
              {...(onNewTask ? { onNewTask: () => onNewTask(b) } : {})}
              trigger={
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={`Actions for ${b.name}`}
                  className={ACTION}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EllipsisIcon aria-hidden="true" className="size-3.5" />
                </button>
              }
            />
          </span>
        </div>
      </li>
    );
  };

  const pageIds = new Set(pages.map((p) => p.id));
  /** A page's children in order; a page whose parent is not listed sits at its space's top. */
  const pagesUnder = (spaceId: string, parentId: string | null) =>
    pages
      .filter(
        (p) =>
          p.space_id === spaceId &&
          (parentId === null
            ? p.parent_page_id === null || !pageIds.has(p.parent_page_id)
            : p.parent_page_id === parentId),
      )
      .sort(siblingOrder);
  /** MULTI-SPACE-ACCESS: the live pages of other spaces shared into this one. */
  const sharedIn = (spaceId: string) =>
    pages
      .filter(
        (p) =>
          p.space_id !== spaceId &&
          sharedPages.some((x) => x.object_id === p.id && x.space_id === spaceId),
      )
      .sort((a, b) => a.title.localeCompare(b.title));
  const pagesIn = (spaceId: string) =>
    pages.filter((p) => p.space_id === spaceId).length + sharedIn(spaceId).length;
  const spaceNameOf = (id: string) => spaces.find((x) => x.id === id)?.name ?? null;
  /** A page shown in a space it is shared into: a leaf with the shared marker. */
  const sharedRow = (p: PageRow, s: Space, level: number): ReactNode => {
    const href = `/c/${slug}/work/p/${p.id}`;
    return (
      <li key={`shared:${s.id}:${p.id}`} role="none">
        <div className="group/tree-node relative">
          <Link
            to="/c/$slug/work/p/$pageId"
            params={{ slug, pageId: p.id }}
            role="treeitem"
            aria-level={level}
            aria-current={pathname === href ? "page" : undefined}
            tabIndex={-1}
            data-node-id={`shared:${s.id}:${p.id}`}
            data-page-row=""
            data-shared=""
            className={ROW}
          >
            <Indent level={level} />
            <Chevron open={false} hidden onToggle={() => undefined} label={p.title} />
            <FileTextIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
            <span className="min-w-0 flex-1 truncate">{p.title}</span>
            <SharedMarker home={spaceNameOf(p.space_id)} />
          </Link>
        </div>
      </li>
    );
  };
  /** WORKFORCE-SPACES: Docs shows for a space with pages, or one whose "+" › Add docs was chosen. */
  const hasDocs = (s: Space) =>
    s.kind !== "personal" && (s.settings.docs === true || pagesIn(s.id) > 0);
  const openDocs = (s: Space) =>
    void navigate({
      to: "/c/$slug/work/docs",
      params: { slug },
      search: { space: s.id } as never,
    });
  /**
   * "+" › Add docs: the space's owners (admins here) turn its Docs row on and land on its Docs
   * screen; anyone else starts its first page (the New doc flow), which brings the row with it.
   */
  const addDocs = (s: Space) => {
    if (hasDocs(s)) return openDocs(s);
    if (!admin) return onNewPage?.({ spaceId: s.id, parentPageId: null });
    api.client.spaces
      .update({
        params: { co: company.id, id: s.id },
        body: { settings: { ...s.settings, docs: true } },
      })
      .then(async () => {
        await invalidateWork(queryClient, company.id);
        openDocs(s);
      })
      .catch(() => toast.add({ title: `Could not add docs to ${s.name}`, type: "error" }));
  };
  const canAddDocs = (s: Space) => s.kind !== "personal" && (admin || Boolean(onNewPage));

  const newPageButton = (target: NewPageTarget, label: string) =>
    onNewPage ? (
      <button
        type="button"
        tabIndex={-1}
        aria-label={label}
        title={label}
        data-slot="tree-new-page"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onNewPage(target);
        }}
        className={ACTION}
      >
        <PlusIcon aria-hidden="true" className="size-3.5" />
      </button>
    ) : null;

  const pageRow = (p: PageRow, level: number, seen: ReadonlySet<string>): ReactNode => {
    const kids = seen.has(p.id) ? [] : pagesUnder(p.space_id, p.id);
    const open = !closed.has(p.id);
    const href = `/c/${slug}/work/p/${p.id}`;
    const next = new Set(seen).add(p.id);
    return (
      <li key={p.id} role="none">
        <div className="group/tree-node relative">
          <Link
            to="/c/$slug/work/p/$pageId"
            params={{ slug, pageId: p.id }}
            role="treeitem"
            aria-level={level}
            aria-expanded={kids.length ? open : undefined}
            aria-current={pathname === href ? "page" : undefined}
            tabIndex={-1}
            data-node-id={p.id}
            data-page-row=""
            className={cn(ROW, onNewPage && "pr-8")}
          >
            <Indent level={level} />
            <Chevron
              open={open}
              hidden={!kids.length}
              onToggle={() => toggle(p.id)}
              label={p.title}
            />
            <FileTextIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
            <span className="min-w-0 flex-1 truncate">{p.title}</span>
            {p.restricted ? (
              <LockIcon aria-label="Restricted" className="size-3 shrink-0 text-faint" />
            ) : null}
          </Link>
          <span className={ACTIONS}>
            {newPageButton(
              { spaceId: p.space_id, parentPageId: p.id, parentTitle: p.title },
              `New page under ${p.title}`,
            )}
          </span>
        </div>
        {kids.length && open ? (
          // biome-ignore lint/a11y/useSemanticElements: a tree branch, not a form group.
          <ul role="group" className="m-0 flex list-none flex-col gap-px p-0">
            {kids.map((k) => pageRow(k, level + 1, next))}
          </ul>
        ) : null}
      </li>
    );
  };

  /** One "Docs" row per space: the Docs screen filtered to it; its page tree behind a chevron. */
  const docsRow = (s: Space, level: number) => {
    const home = pagesUnder(s.id, null);
    const shared = sharedIn(s.id);
    const own = [...home, ...shared];
    const count = pagesIn(s.id);
    const key = `docs:${s.id}`;
    const open = docsIsOpen(key);
    const current = pathname === `/c/${slug}/work/docs` && search.space === s.id;
    return (
      <li key={key} role="none">
        <div className="group/tree-node relative">
          <Link
            to="/c/$slug/work/docs"
            params={{ slug }}
            search={{ space: s.id } as never}
            role="treeitem"
            aria-level={level}
            aria-expanded={own.length ? open : undefined}
            aria-current={current ? "page" : undefined}
            aria-label={`Docs of ${s.name}, ${count}`}
            tabIndex={-1}
            data-node-id={key}
            data-docs-row=""
            className={cn(ROW, "pr-8")}
          >
            <Indent level={level} />
            <Chevron
              open={open}
              hidden={!own.length}
              onToggle={() => toggle(key)}
              label={`the docs of ${s.name}`}
            />
            <BookOpenIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
            <span className="min-w-0 flex-1 truncate">Docs</span>
            {count ? (
              <span className="text-2xs text-muted-foreground tabular-nums group-hover/tree-node:invisible">
                {count}
              </span>
            ) : null}
          </Link>
          <span className={ACTIONS}>
            {s.archived_at
              ? null
              : newPageButton({ spaceId: s.id, parentPageId: null }, `New page in ${s.name}`)}
          </span>
        </div>
        {own.length && open ? (
          // biome-ignore lint/a11y/useSemanticElements: a tree branch, not a form group.
          <ul role="group" className="m-0 flex list-none flex-col gap-px p-0">
            {home.map((p) => pageRow(p, level + 1, new Set()))}
            {shared.map((p) => sharedRow(p, s, level + 1))}
          </ul>
        ) : null}
      </li>
    );
  };

  const spaceRow = (s: Space, level: number, groupId: string | null): ReactNode => {
    const Icon = SPACE_ICON[s.kind];
    const own = boardsOf(boards, s.id);
    const open = !closed.has(s.id);
    const href = `/c/${slug}/work/s/${s.id}`;
    const target = over?.id === s.id;
    const fav = { kind: "space" as const, id: s.id };
    const starred = favorites.has(fav);
    const docs = hasDocs(s);
    const hasChildren = own.length > 0 || docs;
    const clientHealth = s.kind === "client" ? health.get(s.id) : undefined;
    return (
      <li key={s.id} role="none">
        <div className="group/tree-node relative">
          <Link
            to="/c/$slug/work/s/$spaceId"
            params={{ slug, spaceId: s.id }}
            role="treeitem"
            aria-level={level}
            aria-expanded={hasChildren ? open : undefined}
            aria-current={pathname === href ? "page" : undefined}
            tabIndex={s.id === firstId ? 0 : -1}
            data-node-id={s.id}
            data-space-row=""
            draggable
            className={cn(
              ROW,
              "pr-14 group-hover/tree-node:pr-32 group-focus-within/tree-node:pr-32",
              target && (over?.kind === "inside" ? DROP_INSIDE : DROP_BEFORE),
              dragSpace === s.id && "opacity-40",
            )}
            onDragStart={(e: DragEvent) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData(SPACE_MIME, s.id);
              setDragSpace(s.id);
            }}
            onDragEnd={() => {
              setDragSpace(null);
              setOver(null);
            }}
            onDragOver={(e: DragEvent) => {
              // BOARD-ORDER: a board dropped on a space goes to the end of its boards.
              if (dragBoard && !s.archived_at && s.kind !== "personal") {
                e.preventDefault();
                setOver({ id: s.id, kind: "inside" });
                return;
              }
              if (!dragSpace || dragSpace === s.id) return;
              e.preventDefault();
              setOver({ id: s.id, kind: "before" });
            }}
            onDragLeave={() => setOver((o) => (o?.id === s.id && dragBoard ? null : o))}
            onDrop={(e: DragEvent) => {
              if (dragBoard) {
                e.preventDefault();
                setOver(null);
                moveBoard(dragBoard, s.id, null);
                setDragBoard(null);
                return;
              }
              if (!dragSpace) return;
              e.preventDefault();
              // Before this space at the top level; a grouped target means "into its group".
              dropSpace(groupId ? { kind: "group", groupId } : { kind: "top", beforeId: s.id });
            }}
          >
            <Indent level={level} />
            {/* wp/ACTIONS: the visible handle of the space's drag (move), as a board row's. */}
            <GripVerticalIcon
              aria-hidden="true"
              data-slot="tree-grip"
              className="absolute left-1 size-3.5 cursor-grab text-faint opacity-0 group-hover/tree-node:opacity-100 pointer-coarse:hidden"
            />
            <Chevron
              open={open}
              hidden={!hasChildren}
              onToggle={() => toggle(s.id)}
              label={s.name}
            />
            <Icon aria-hidden="true" className="size-4 shrink-0 text-faint" />
            <span className="min-w-0 flex-1 truncate">{s.name}</span>
            {clientHealth ? <HealthDot health={clientHealth} /> : null}
          </Link>
          {s.archived_at ? null : (
            <span className={ACTIONS}>
              {starAction(s.name, starred, () => favorites.toggle(fav, s.name))}
              {onNewBoard || canAddDocs(s) ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={`Add to ${s.name}`}
                        title={`Add to ${s.name}`}
                        className={ACTION}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PlusIcon aria-hidden="true" className="size-3.5" />
                      </button>
                    }
                  />
                  <DropdownMenuContent align="end" className="min-w-40">
                    {onNewBoard ? (
                      <DropdownMenuItem onClick={() => onNewBoard(s.id)}>
                        <SquareKanbanIcon aria-hidden="true" />
                        Add board
                      </DropdownMenuItem>
                    ) : null}
                    {canAddDocs(s) ? (
                      <DropdownMenuItem onClick={() => addDocs(s)}>
                        <BookOpenIcon aria-hidden="true" />
                        Add docs
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              <SpaceMenu
                api={api}
                company={company}
                space={s}
                star={{ on: starred, toggle: () => favorites.toggle(fav, s.name) }}
                icons={renameArchive(s.name)}
                {...(onNewBoard ? { onNewBoard: () => onNewBoard(s.id) } : {})}
                {...(onSpaceSettings && s.kind !== "personal"
                  ? { onSettings: () => onSpaceSettings(s) }
                  : {})}
                {...(onNewPage
                  ? { onNewDoc: () => onNewPage({ spaceId: s.id, parentPageId: null }) }
                  : {})}
                trigger={
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={`Actions for ${s.name}`}
                    className={ACTION}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EllipsisIcon aria-hidden="true" className="size-3.5" />
                  </button>
                }
              />
            </span>
          )}
        </div>
        {hasChildren && open ? (
          // biome-ignore lint/a11y/useSemanticElements: a tree branch, not a form group.
          <ul role="group" className="m-0 flex list-none flex-col gap-px p-0">
            {own.map((b) => boardRow(b, level + 1))}
            {docs ? docsRow(s, level + 1) : null}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <>
      <ul
        ref={root}
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: an ARIA tree has no native element.
        role="tree"
        aria-label="Spaces"
        className="m-0 flex list-none flex-col gap-px p-0"
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          // Roving tabindex: the first row is the tab stop until another row takes focus.
          if (e.target.getAttribute("role") === "treeitem") (e.target as HTMLElement).tabIndex = 0;
        }}
      >
        {rows.map((r) => {
          if (r.kind === "space") {
            const s = spaces.find((x) => x.id === r.spaceId);
            return s ? spaceRow(s, 1, null) : null;
          }
          const g = r.group;
          const open = !closed.has(g.id);
          const target = over?.id === g.id;
          return (
            <li key={g.id} role="none">
              <div className="group/tree-node relative">
                <div
                  role="treeitem"
                  aria-level={1}
                  aria-expanded={open}
                  aria-selected={false}
                  tabIndex={g.id === firstId ? 0 : -1}
                  data-node-id={g.id}
                  title={`${g.name}: a group of spaces in your sidebar. Drag a space here to add it.`}
                  className={cn(
                    ROW,
                    "font-semibold group-hover/tree-node:pr-14 group-focus-within/tree-node:pr-14",
                    target && DROP_INSIDE,
                  )}
                  onClick={() => toggle(g.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(g.id);
                    }
                  }}
                  onDragOver={(e) => {
                    if (!dragSpace) return;
                    e.preventDefault();
                    setOver({ id: g.id, kind: "inside" });
                  }}
                  onDragLeave={() => setOver((o) => (o?.id === g.id ? null : o))}
                  onDrop={(e) => {
                    e.preventDefault();
                    dropSpace({ kind: "group", groupId: g.id });
                  }}
                >
                  <Indent level={1} />
                  <ChevronRightIcon
                    aria-hidden="true"
                    className={cn(
                      "size-3.5 shrink-0 text-faint transition-transform",
                      open && "rotate-90",
                    )}
                  />
                  <FolderIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
                  <span className="min-w-0 flex-1 truncate">{g.name}</span>
                  <span className="text-2xs font-normal text-muted-foreground group-hover/tree-node:invisible group-focus-within/tree-node:invisible">
                    {g.spaceIds.length}
                  </span>
                </div>
                {/* wp/ACTIONS: rename and remove the group (the person's own arrangement). */}
                <span className={ACTIONS}>
                  {action(
                    `Rename ${g.name}`,
                    () => setGroupEdit({ kind: "rename", group: g }),
                    <PencilIcon aria-hidden="true" className="size-3.5" />,
                  )}
                  {action(
                    `Remove ${g.name}`,
                    () => setGroupEdit({ kind: "remove", group: g }),
                    <Trash2Icon aria-hidden="true" className="size-3.5" />,
                    "hover:text-destructive",
                  )}
                </span>
              </div>
              {open && g.spaceIds.length ? (
                // biome-ignore lint/a11y/useSemanticElements: a tree branch, not a form group.
                <ul role="group" className="m-0 flex list-none flex-col gap-px p-0">
                  {g.spaceIds.map((id) => {
                    const s = spaces.find((x) => x.id === id);
                    return s ? spaceRow(s, 2, g.id) : null;
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
      {dragSpace ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: a pointer-drag drop zone; the space's menu is not needed to arrange by keyboard (the order is personal only).
        <div
          data-slot="tree-drop-end"
          className={cn(
            "mx-2 mt-1 rounded-md border border-dashed border-strong px-3 py-2 text-xs text-muted-foreground",
            over?.id === "end" && DROP_INSIDE,
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setOver({ id: "end", kind: "inside" });
          }}
          onDrop={(e) => {
            e.preventDefault();
            dropSpace({ kind: "top", beforeId: null });
          }}
        >
          Drop here to move it to the end, out of its group
        </div>
      ) : null}
      {groupEdit?.kind === "rename" ? (
        <RenameGroupDialog
          name={groupEdit.group.name}
          onClose={() => setGroupEdit(null)}
          onSave={(n) => onTreeChange(renameGroup(tree, groupEdit.group.id, n))}
        />
      ) : null}
      <AlertDialog
        open={groupEdit?.kind === "remove"}
        onOpenChange={(o) => !o && setGroupEdit(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove the group {groupEdit?.group.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Its spaces go back to the top of your sidebar. No space is changed, and only your
              sidebar changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (groupEdit) onTreeChange(removeGroup(tree, ids, groupEdit.group.id));
                setGroupEdit(null);
              }}
            >
              Remove group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Indent per tree level (pages nest below boards' level; deeper pages stop at the last step). */
const INDENT = ["w-0", "w-4", "w-8", "w-12", "w-16", "w-20"] as const;

function Indent({ level }: { level: number }) {
  return (
    <span
      aria-hidden="true"
      className={cn("shrink-0", INDENT[Math.min(level, INDENT.length) - 1])}
    />
  );
}

function Chevron({
  open,
  hidden,
  onToggle,
  label,
}: {
  open: boolean;
  hidden: boolean;
  onToggle: () => void;
  label: string;
}) {
  if (hidden) return <span aria-hidden="true" className="size-3.5 shrink-0" />;
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      className="relative z-10 -ml-0.5 flex size-4 shrink-0 items-center justify-center rounded-xs text-faint hover:text-foreground"
    >
      <ChevronRightIcon
        aria-hidden="true"
        className={cn("size-3.5 transition-transform", open && "rotate-90")}
      />
    </button>
  );
}
